import type {
  CloudSecurityDirection,
  PathLookupDecisionEvent,
  Security,
} from '@ipfabric/utils';
import type { FC } from 'react';

import { Link } from '@ipfabric/ui-components/Link/Link';
import { SeverityDetails } from './SeverityDetails';

interface Props {
  event: PathLookupDecisionEvent;
  /** Opens the evaluated machine at the deciding rule. */
  onShowDetail: () => void;
}

/**
 * `undefined` when the evaluator recorded no severity (an invisible policy):
 * the check ran, but nothing in the event says how it ended, so no verdict word
 * is shown rather than guessing one.
 */
type CloudSecurityVerdict = 'allowed' | 'denied' | 'issue' | undefined;

const getVerdict = (event: PathLookupDecisionEvent): CloudSecurityVerdict => {
  switch (event.severityInfo?.name) {
    case 'ACL_PERMIT':
      return 'allowed';
    case 'ACL_DENY':
      return 'denied';
    // Recorded alongside the verdict when the evaluation itself had problems;
    // severity 20, so neither an allow nor a deny.
    case 'ACL_CONFIG_ERROR':
    case 'ACL_PARSING_ERROR':
      return 'issue';
    default:
      return undefined;
  }
};

const directionLabel = (
  direction: CloudSecurityDirection | undefined,
): string => {
  if (direction === undefined) {
    return 'Security check';
  }
  return direction === 'inbound'
    ? 'Inbound security check'
    : 'Outbound security check';
};

const verdictLabel = (
  verdict: CloudSecurityVerdict,
  ignored: boolean,
): string => {
  switch (verdict) {
    case 'allowed':
      return ignored ? ' allowed (not applied)' : ' allowed';
    case 'denied':
      return ' denied';
    case 'issue':
      return ' could not be evaluated';
    default:
      return '';
  }
};

/**
 * What decided, as the link label — the rule's vendor id or name on its own.
 * The point's own default (the implicit deny over every policy attached to it)
 * has no rule to name, and reads the way the tree titles its last box.
 */
const decidedByLabel = (event: PathLookupDecisionEvent): string => {
  if (event.decidingRuleId !== undefined) {
    return event.decidingRuleId;
  }
  return event.isDefaultRule === true ? 'default action' : 'deciding rule';
};

/**
 * The policy the deciding rule was authored in — an enforcement point merges
 * several, so a verdict without an owner says little. Quoted because a vendor
 * name can be an ARN or a bare folder id; not linked, since the tree the row
 * already opens is what draws a box per policy.
 */
const PolicySuffix: FC<{ policy: Security.CloudPolicyRef | undefined }> = ({
  policy,
}) =>
  policy === undefined ? null : (
    <>
      {" in '"}
      <span className='font-bold'>{policy.name ?? policy.id}</span>
      {"'"}
    </>
  );

/**
 * Security row: direction evaluated, verdict, and the rule that reached it
 * inside its policy — the rule links into the evaluated machine, scrolled to
 * that rule (NIM-24555).
 */
export const CloudSecurityCheckCell: FC<Props> = (props) => {
  const { event } = props;
  const verdict = getVerdict(event);
  return (
    <>
      {directionLabel(event.cloudDirection)}
      {verdictLabel(verdict, event.type === 'security check ignored')}
      {event.decidingRule !== undefined && (
        <>
          {event.decidingRuleId === undefined ? ' by ' : ' by rule '}
          <Link onClick={props.onShowDetail} renderAs='button'>
            {decidedByLabel(event)}
          </Link>
          {event.decidingRuleId !== undefined && (
            <PolicySuffix policy={event.cloudPolicy} />
          )}
        </>
      )}
      <SeverityDetails event={event} />
    </>
  );
};
